/**
 * ProposalActivityTab — chronological timeline + Add Activity form
 *
 * Renders inside the existing proposal view modal under the Activity tab.
 * Pulls all events from pos_proposal_events, joins user names via created_by,
 * renders friendly labels per event type. Add Activity form at the top
 * POSTs to pos-proposal-event-log.
 */

import { useState, useEffect, useCallback } from 'react';
import { formatDateTimeShort } from '../../../../utils/formatters';
import type { SupabaseClient } from '@supabase/supabase-js';

interface UserRef {
  first_name?: string;
  last_name?: string;
}

interface EventRow {
  id: string;
  proposal_id: string;
  event_type: string;
  event_data: Record<string, unknown> | null;
  created_at: string;
  created_by: string | null;
  users?: UserRef | null;  // joined via created_by → users(first_name, last_name)
}

interface ProposalActivityTabProps {
  proposalId: string;
  supabase: SupabaseClient;
}

type ActivityType = 'note' | 'call' | 'meeting' | 'other';

const ACTIVITY_TYPE_OPTIONS: { value: ActivityType; label: string; icon: string }[] = [
  { value: 'note', label: 'Note', icon: '📝' },
  { value: 'call', label: 'Call', icon: '📞' },
  { value: 'meeting', label: 'Meeting', icon: '🤝' },
  { value: 'other', label: 'Other', icon: '🔹' },
];

export default function ProposalActivityTab({ proposalId, supabase }: ProposalActivityTabProps) {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add Activity form state
  const [activityType, setActivityType] = useState<ActivityType>('note');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchErr } = await supabase
      .from('pos_proposal_events')
      .select('id, proposal_id, event_type, event_data, created_at, created_by, users:created_by(first_name, last_name)')
      .eq('proposal_id', proposalId)
      .order('created_at', { ascending: false })
      .limit(200);
    if (fetchErr) {
      setError(fetchErr.message);
      setLoading(false);
      return;
    }
    setEvents((data as unknown as EventRow[]) || []);
    setLoading(false);
  }, [proposalId, supabase]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  async function handleSave() {
    const trimmed = body.trim();
    if (!trimmed) return;
    setSaving(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('pos-proposal-event-log', {
        body: { proposal_id: proposalId, activity_type: activityType, body: trimmed },
      });
      if (fnErr) throw fnErr;
      if (data?.error) throw new Error(data.error);
      setBody('');
      setActivityType('note');
      await loadEvents();
    } catch (err) {
      setError((err as Error).message || 'Failed to save activity');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {/* Add Activity form */}
      <div className="view-section" style={{ background: 'var(--color-bg)', padding: '0.75rem', borderRadius: 'var(--radius-md)', marginBottom: '0.75rem' }}>
        <h3 className="section-title mt-0">Add activity</h3>
        <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
          {ACTIVITY_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`page-tab ${activityType === opt.value ? 'active' : ''}`}
              style={{ fontSize: 'var(--text-md)', padding: '0.25rem 0.625rem' }}
              onClick={() => setActivityType(opt.value)}
            >
              {opt.icon} {opt.label}
            </button>
          ))}
        </div>
        <textarea
          className="form-input"
          rows={3}
          placeholder={
            activityType === 'note' ? "What's the latest? Free-text note about this proposal." :
            activityType === 'call' ? 'Who did you speak to and what was said?' :
            activityType === 'meeting' ? 'Who did you meet, where, and what came out of it?' :
            'Anything you want to record against this proposal.'
          }
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={5000}
          style={{ resize: 'vertical', fontSize: 'var(--text-base)', marginBottom: '0.5rem' }}
        />
        <div className="flex justify-between items-center">
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)' }}>
            {body.length}/5000
          </span>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={handleSave}
            disabled={!body.trim() || saving}
          >
            {saving ? 'Saving…' : 'Save activity'}
          </button>
        </div>
      </div>

      {/* Timeline feed */}
      <div className="view-section">
        <h3 className="section-title">Timeline</h3>
        {error && (
          <div className="form-error mb-sm">{error}</div>
        )}
        {loading ? (
          <div className="text-sm text-muted">Loading…</div>
        ) : events.length === 0 ? (
          <div className="text-sm text-muted">No activity yet. Add the first note above.</div>
        ) : (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {events.map((evt) => <ActivityRow key={evt.id} evt={evt} />)}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// One row in the timeline
// =============================================================================

interface ActivityRowProps {
  evt: EventRow;
}

function ActivityRow({ evt }: ActivityRowProps) {
  const ts = new Date(evt.created_at);
  const dateLabel = formatDateTimeShort(ts);
  const userName = evt.users
    ? `${evt.users.first_name || ''} ${evt.users.last_name || ''}`.trim()
    : '';

  const { icon, label, body } = friendlyLabel(evt, userName);

  // Customer-side time_on_page is high-volume noise — fold these out of the
  // top-level timeline. They'll still show up if needed via the analytics block.
  if (evt.event_type === 'time_on_page') return null;

  return (
    <div style={{ display: 'flex', gap: '0.5rem', padding: '0.5rem 0', borderBottom: '1px solid var(--color-border)' }}>
      <div style={{ width: '24px', flexShrink: 0, textAlign: 'center' }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 'var(--text-md)', lineHeight: 1.4 }}>{label}</div>
        {body && (
          <div style={{ marginTop: '0.25rem', fontSize: 'var(--text-md)', color: 'var(--color-text-light)', whiteSpace: 'pre-wrap' }}>
            {body}
          </div>
        )}
      </div>
      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', flexShrink: 0, whiteSpace: 'nowrap' }}>
        {dateLabel}
      </div>
    </div>
  );
}

// =============================================================================
// Map event_type → icon + readable label
// =============================================================================

function friendlyLabel(evt: EventRow, userName: string): { icon: string; label: string; body?: string } {
  const data = (evt.event_data || {}) as Record<string, unknown>;
  const who = userName || 'Someone';

  switch (evt.event_type) {
    case 'view':
      return {
        icon: '👁',
        label: `Customer viewed${data.is_mobile ? ' (mobile)' : ' (desktop)'}`,
      };
    case 'cta_click':
      return {
        icon: '🖱',
        label: `Customer clicked ${(data.target as string) || 'a button'}`,
      };
    case 'sent_via_email':
      return { icon: '📧', label: `${who} sent via email` };
    case 'followup_sent':
      return {
        icon: '📩',
        label: `${who} sent the follow-up email${data.resent ? ' again' : ''}`,
        body: data.to ? `To ${String(data.to)}, from ${String(data.from_name || data.from || '')}` : undefined,
      };
    case 'followup_whatsapp_sent':
      return { icon: '💬', label: `${who} sent the follow-up on WhatsApp` };
    case 'sent_via_whatsapp':
      return { icon: '💬', label: `${who} sent via WhatsApp` };
    case 'sent_via_sms':
      return { icon: '✉️', label: `${who} sent via SMS` };
    case 'submitted_for_approval':
      return { icon: '📤', label: `${who} submitted for review` };
    case 'approved_by_admin':
      return { icon: '✅', label: `${who} approved the proposal` };
    case 'rejected_by_admin':
      return {
        icon: '❌',
        label: `${who} rejected the proposal`,
        body: data.reason ? String(data.reason) : undefined,
      };
    case 'accepted_by_customer':
      return { icon: '✍️', label: 'Customer accepted the proposal' };
    case 'expired':
      return { icon: '⏰', label: 'Proposal expired' };
    case 'customer_attached':
      return { icon: '🔗', label: 'Customer record linked to proposal' };
    case 'customer_attach_failed':
      return { icon: '⚠️', label: 'Customer link failed (system)' };
    case 'manual_activity': {
      const activityType = (data.activity_type as string) || 'note';
      const icons: Record<string, string> = { note: '📝', call: '📞', meeting: '🤝', other: '🔹' };
      const labels: Record<string, string> = { note: 'Note', call: 'Call', meeting: 'Meeting', other: 'Activity' };
      return {
        icon: icons[activityType] || '📝',
        label: `${who} — ${labels[activityType] || 'Activity'}`,
        body: data.body ? String(data.body) : undefined,
      };
    }
    case 'manual_stage_change': {
      const from = (data.from as string) || 'auto';
      const to = (data.to as string) || 'auto';
      const arrow = `${from} → ${to}`;
      return {
        icon: '📌',
        label: `${who} pinned card`,
        body: arrow,
      };
    }
    case 'marked_lost':
      return {
        icon: '❌',
        label: `${who} marked as lost`,
        body: data.reason ? String(data.reason) : undefined,
      };
    case 'marked_accepted_admin':
      return {
        icon: '✍️',
        label: `${who} recorded customer acceptance`,
        body: data.note ? String(data.note) : undefined,
      };
    case 'unmarked_accepted':
      return {
        icon: '↩️',
        label: `${who} unmarked acceptance`,
        body: data.reason ? String(data.reason) : undefined,
      };
    case 're_engaged':
      return {
        icon: '↩️',
        label: `${who} re-engaged a lost proposal`,
      };
    case 'recalled_to_drafting': {
      const fromStatus = (data.from_status as string) || 'unknown';
      return {
        icon: '↪️',
        label: `${who} recalled to drafting (from ${fromStatus})`,
        body: data.reason ? String(data.reason) : undefined,
      };
    }
    default:
      return { icon: '·', label: evt.event_type };
  }
}
